import { LoggerEvent, LoggerProvider, logRocketFuzzySearch } from './../logger/logger';
import { Injectable } from '@angular/core';
import LogRocket from 'logrocket';
import {APP_CONFIG, Env} from '../../environment';
import { AppVersion } from '@ionic-native/app-version';
import { Platform } from 'ionic-angular';
import { AppVersionProvider } from '../app-version/app-version';

@Injectable()
export class LogRocketProvider {

  version = 'Unknown';
  allowedApiCallsForLogging = [
    'api/Account/Create',
    'api/Customer/ComplianceQuestions/save',
    'order/additionalfields',
    'customer-profile/additionalfields',
    'order/validate',
    'order',
    'tracking/order/status',
    'moneytransfercalculator/calculate/v2'
  ];
  allowedApiCallsForLoggingList = [];
  allowedApiCallsForLoggingEnv = '';
  logRocketEnterPromise;
  logRocketEnterPromiseResolve;
  logRocketInitPromise;
  logRocketInitPromiseResolve;
  segmentSessionURL;

  constructor(
    private appVersion: AppVersion, private platform: Platform, private appVersionProvider: AppVersionProvider) {

      this.logRocketEnterPromise = new Promise((resolve) => {
        this.logRocketEnterPromiseResolve = resolve;
      });
      this.logRocketInitPromise = new Promise((resolve) => {
        this.logRocketInitPromiseResolve = resolve;
      });

    this.initAllowedApiCallsForLogging();
  }


  initAllowedApiCallsForLogging() {
    const env = APP_CONFIG.remittanceApiBaseUrl + '|' + APP_CONFIG.API_URL;
    if (this.allowedApiCallsForLoggingEnv !== env) {
      this.allowedApiCallsForLoggingList = [];
      this.allowedApiCallsForLogging.forEach((url) => {
        this.allowedApiCallsForLoggingList.push(APP_CONFIG.remittanceApiBaseUrl + url);
        this.allowedApiCallsForLoggingList.push(APP_CONFIG.API_URL + url);
      })
      this.allowedApiCallsForLoggingEnv = env;
    }
  }

  async resetLogRocketSession() {
    LoggerProvider.info(this, 'Reset Logrocket session - Username changed');
    LoggerProvider.logRocket = null;
    LogRocket.startNewSession();
    this.init();
  }

  /** Resolves once the document's 'load' event has fired (immediately if it already has). */
  static afterPageLoad(): Promise<void> {
    if (document.readyState === 'complete')
      return Promise.resolve();
    return new Promise<void>((resolve) => {
      const onLoad = () => {
        window.removeEventListener('load', onLoad);
        resolve();
      };
      window.addEventListener('load', onLoad);
    });
  }

  async init(){
    LoggerProvider.info(this, 'LogRocketProvider started');

    try{
      this.version = await this.appVersionProvider.getVersionNumber();
    } catch(ex){
      LoggerProvider.info(this, 'Failed to retrieve app version');
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to retrieve version.');
    }

    const isProd = APP_CONFIG.env === Env.PROD;
    const network = isProd
      ? {
          ...APP_CONFIG.logrocket.network,
          requestSanitizer: (request) => {
            this.sanitizeAuthHeaders(request);
            this.sanitizeRequestBody(request);
            return logRocketFuzzySearch.requestSanitizer(request);
          },
          responseSanitizer: (response) => {
            this.sanitizeAuthHeaders(response);
            return logRocketFuzzySearch.responseSanitizer(response);
          },
        }
      : APP_CONFIG.logrocket.network;

    // LogRocket.init() inserts <script async src="https://cdn.logr-ingest.com/logger-1.min.js">; inserted
    // before the page 'load' event it delays 'load', which both Cordova shells wait for
    // (Android: 20 s watchdog -> "Application Error"; iOS: WebView stays hidden). Wait for 'load' first.
    await LogRocketProvider.afterPageLoad();

    LogRocket.init(APP_CONFIG.logrocket.key, {
      release: this.version,
      console:  APP_CONFIG.logrocket.console,
      dom: APP_CONFIG.logrocket.dom,
      network,
    });
    LoggerProvider.logRocket = LogRocket;

    if (this.logRocketInitPromiseResolve)
      this.logRocketInitPromiseResolve(true);
    this.logRocketInitPromise = null;
    this.logRocketInitPromiseResolve = null;

    LogRocket.getSessionURL(sessionURL => {
      this.segmentSessionURL = sessionURL;
      this.logRocketEnterPromiseResolve(true);
      this.logRocketEnterPromise = null;
    });
  }

  getSegmentSessionURL() {
    return this.segmentSessionURL;
  }

  private sanitizeAuthHeaders(data: { headers?: { [key: string]: string } }) {
    if (!data || !data.headers) return;
    for (const key in data.headers) {
      const header = key.toLowerCase();
      if (header === 'authorization' || header === 'bearer') {
        data.headers[key] = '*';
      }
    }
  }

  private sanitizeRequestBody(request: { url?: string; method?: string; body?: any }) {
    const sanitizeBodyUrls = ['.onfido.com'];  // if we need to add more hosts later, just append here
    if (request.url && sanitizeBodyUrls.some(url => request.url.indexOf(url) !== -1)) {
      if (request.method !== 'GET') {
        request.body = undefined;
      }
    }
  }

  // check if we need to log to logrocket for certain api calls
  tologApiCall(url): boolean {
    this.initAllowedApiCallsForLogging();
    return this.allowedApiCallsForLoggingList.some(substring => url.includes(substring));
  }

  toLogRocketConsole(level, loggerEvent, url, duplicateLogBody) {
    LogRocket.log('Logging to LogRocket Console: ' + 'Level: ' + level, ' / Event: ' + loggerEvent, ' / Scope: ' + LoggerProvider.scope + ' / Message: ' + 'POST ' + url, duplicateLogBody);
  }

  identify(userId: any = '', traits = {}){
    traits = {
      ...traits,
      version: this.version,
      deviceId: LoggerProvider.uuid,
      amplitudeSessionId: LoggerProvider.amplitudeSessionId,
      sessionId: LoggerProvider.sessionId,
      country: APP_CONFIG.region.defaultCountryFrom,
      brand: APP_CONFIG.branding.branding,
    };
    LogRocket.identify(userId, traits);
  }

  track(eventName: string, traits={}) {
    const masked = LoggerProvider.maskFuzzy(traits);
    const maskedTraits = (masked && typeof masked === 'object') ? masked : {};

    const properties: any = {
      ...this.sanitizeTrackProperties(maskedTraits),
      version: this.version,
      deviceId: LoggerProvider.uuid,
      amplitudeSessionId: LoggerProvider.amplitudeSessionId,
      sessionId: LoggerProvider.sessionId,
      brand: APP_CONFIG.branding.branding,
    };
    if (properties.country === undefined)
      properties.country = APP_CONFIG.region.defaultCountryFrom;
    LogRocket.track(eventName, properties);
  }

  sanitizeTrackProperties(traits: any = {}): { [key: string]: any } {
    const MAX_PROPERTIES = 2000;
    const MAX_NAME_LENGTH = 100;
    const result = {};
    let count = 0;
    const isPrimitive = (value) => typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string';
    const addProperty = (key: string, value) => {
      if (count >= MAX_PROPERTIES) return;
      if (!key || key.length > MAX_NAME_LENGTH) return;
      result[key] = value;
      count++;
    };
    const flatten = (obj, prefix: string) => {
      if (!obj) return;
      for (const rawKey in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, rawKey)) continue;
        const value = obj[rawKey];
        const key = prefix ? `${prefix}_${rawKey}` : rawKey;
        if (value === null || value === undefined) continue;
        if (isPrimitive(value))
          addProperty(key, value);
        else if (Array.isArray(value)) {
          if (value.length && value.every(isPrimitive))
            addProperty(key, value);
        }
        else if (typeof value === 'object')
          flatten(value, key);
      }
    };
    flatten(traits, '');
    return result;
  }

  setDisabled(disabled) {
    // TODO !!!!!!!!!!! Should be improved as it's not officially supported
    const logRocket: any = LogRocket;
    if (logRocket._logger)
      logRocket._logger._isDisabled = disabled;
  }
}



// WEBPACK FOOTER //
// ./src/providers/log-rocket/log-rocket.ts